import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';

// Extend MUI Radio to support 'large' size
declare module '@mui/material/Radio' {
  interface RadioPropsSizeOverrides {
    large: true;
  }
}

export const MuiRadioTheme = (mode: PaletteMode): Components<Theme>['MuiRadio'] => {
  const { componentTokens, tokens } = themePalette(mode);

  return {
    defaultProps: {
      disableRipple: true,
      disableFocusRipple: true,
      disableTouchRipple: true,
      size: 'medium',
    },
    styleOverrides: {
      root: {
        padding: 0,

        // Default size (medium = 26px)
        '& .MuiSvgIcon-root': {
          width: '16px',
          height: '16px',
        },

        // Unchecked state - target outer circle
        '& .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.notSelected.stroke.resting,
          fill: componentTokens.selectors.colors.radio.notSelected.background.resting,
          transition: 'stroke 0.15s linear, fill 0.15s linear',
        },

        // Unchecked state - inner dot is hidden by default (no fill)
        '& .MuiSvgIcon-root circle:last-of-type': {
          fill: 'transparent',
        },

        // Hover on unchecked
        '&:hover .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.notSelected.stroke.hover,
          fill: componentTokens.selectors.colors.radio.notSelected.background.hover,
        },

        // Focus states
        '&.Mui-focusVisible .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.notSelected.stroke.hover,
          fill: componentTokens.selectors.colors.radio.notSelected.background.hover,
        },
        '&:focus-within .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.notSelected.stroke.hover,
          fill: componentTokens.selectors.colors.radio.notSelected.background.hover,
        },

        // Checked state - color the outer circle
        '&.Mui-checked .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.selected.stroke.resting,
          fill: componentTokens.selectors.colors.radio.selected.background.resting,
        },

        // Checked state - show inner dot with fill
        '&.Mui-checked .MuiSvgIcon-root circle:last-of-type': {
          fill: componentTokens.selectors.colors.radio.selected.assets.base,
        },

        // Hover on checked
        '&.Mui-checked:hover .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.selected.stroke.hover,
          fill: componentTokens.selectors.colors.radio.selected.background.hover,
        },
        '&.Mui-checked:hover .MuiSvgIcon-root circle:last-of-type': {
          fill: componentTokens.selectors.colors.radio.selected.assets.base,
        },

        // Focus on checked
        '&.Mui-checked.Mui-focusVisible .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.selected.stroke.hover,
          fill: componentTokens.selectors.colors.radio.selected.background.hover,
        },
        '&.Mui-checked.Mui-focusVisible .MuiSvgIcon-root circle:last-of-type': {
          fill: componentTokens.selectors.colors.radio.selected.assets.base,
        },
        '&.Mui-checked:focus-within .MuiSvgIcon-root circle:first-of-type': {
          stroke: componentTokens.selectors.colors.radio.selected.stroke.hover,
          fill: componentTokens.selectors.colors.radio.selected.background.hover,
        },
        '&.Mui-checked:focus-within .MuiSvgIcon-root circle:last-of-type': {
          fill: componentTokens.selectors.colors.radio.selected.assets.base,
        },

        // Disabled state
        '&.Mui-disabled': {
          opacity: 0.5,
        },

        // Invalid/error state styling - target circles
        '&.MuiRadio-colorError .MuiSvgIcon-root circle:first-of-type': {
          stroke: tokens.error,
        },
        '&.MuiRadio-colorError:hover .MuiSvgIcon-root circle:first-of-type': {
          stroke: tokens.error,
        },
        '&.MuiRadio-colorError.Mui-focusVisible .MuiSvgIcon-root circle:first-of-type': {
          stroke: tokens.error,
        },
        '&.MuiRadio-colorError:focus-within .MuiSvgIcon-root circle:first-of-type': {
          stroke: tokens.error,
        },
        '&.MuiRadio-colorError.Mui-checked .MuiSvgIcon-root circle:first-of-type': {
          stroke: tokens.error,
        },
        '&.MuiRadio-colorError.Mui-checked .MuiSvgIcon-root circle:last-of-type': {
          fill: tokens.error,
        },
        '&.MuiRadio-colorError.Mui-checked:hover .MuiSvgIcon-root circle:last-of-type': {
          fill: tokens.error,
        },
        '&.MuiRadio-colorError.Mui-checked.Mui-focusVisible .MuiSvgIcon-root circle:last-of-type': {
          fill: tokens.error,
        },
        '&.MuiRadio-colorError.Mui-checked:focus-within .MuiSvgIcon-root circle:last-of-type': {
          fill: tokens.error,
        },
      },
    },
  };
};
